import { Box, Button, MenuItem, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { LegalForm, TenantCountryCodeEnum } from 'probonio-shared-ui/api';
import { apis } from 'probonio-shared-ui/module/api';
import { useTenant, useTenantID } from 'probonio-shared-ui/module/me';
import { LEGAL_FORM_LABELS } from 'probonio-shared-ui/utils/legalFormLabels';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BasicDialog, DialogState } from '../../../component/dialog';
import { DialogLoadingButton } from '../../../component/dialog/DialogLoadingButton';
import { SelectControl, TextFieldControl } from '../../../component/form';
import { CheckboxControl } from '../../../component/form/CheckboxControl';

interface BikeleasingCustomerValues {
  companyForm: LegalForm;
  companyName: string;
  streetAndHouseNumber: string;
  postalCode: string;
  city: string;
  country: string;
  olderThan1Year: boolean;
}

interface Props {
  dialogState: DialogState;
  onSuccess: () => void;
}

export const BikeLeasingCustomerDialog: React.FC<Props> = ({ dialogState, onSuccess }) => {
  const { t } = useTranslation('extensions');
  const { tenant } = useTenant();
  const getTenantId = useTenantID();
  const { enqueueSnackbar } = useSnackbar();

  const { control, handleSubmit, formState } = useForm<BikeleasingCustomerValues>({
    defaultValues: {
      companyForm: tenant?.legalForm,
      companyName: tenant?.name,
      streetAndHouseNumber: tenant?.street,
      postalCode: tenant?.postalCode,
      city: tenant?.city,
      country: tenant?.addressCountry ?? tenant?.countryCode,
      olderThan1Year: false,
    },
    mode: 'onChange',
  });

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (customerData: BikeleasingCustomerValues) =>
      apis.bikeLeasingV2.requestCustomer({
        bikeleasingCustomerDTO: customerData,
        tenantId: getTenantId(),
      }),
    onSuccess: () => {
      dialogState.handleClose();
      onSuccess();
    },
    onError: () => {
      enqueueSnackbar(t('bikeleasing_v2.integration.form.error'), { variant: 'error' });
    },
  });

  const handleClick = useCallback(
    async (values: BikeleasingCustomerValues) => {
      await mutateAsync(values);
    },
    [mutateAsync],
  );

  return (
    <BasicDialog
      dialogState={dialogState}
      hideCloseIcon
      title={
        <Typography variant="inherit" sx={{ textWrap: 'balance' }}>
          {t('bikeleasing_v2.integration.title')}
        </Typography>
      }
      maxWidth="sm"
      fullWidth
      content={
        <Stack>
          <Typography variant="body1" sx={{ marginBottom: 4 }}>
            {t('bikeleasing_v2.integration.description')}
          </Typography>
          <form>
            <Stack spacing={2}>
              <TextFieldControl
                control={control}
                name="companyName"
                label={t('tenantModule:paymentSettings.fields.tenantName')}
                rules={{ required: true }}
                data-test-id="bikeleasing-integration-company-name-input"
              />
              <SelectControl
                control={control}
                name="companyForm"
                label={t('tenantModule:paymentSettings.fields.tenantLegalForm')}
                rules={{ required: true }}
                margin="dense"
                data-test-id="bikeleasing-integration-company-form-select"
              >
                {Object.values(LegalForm).map(form => (
                  <MenuItem key={form} value={form}>
                    {LEGAL_FORM_LABELS[form]}
                  </MenuItem>
                ))}
              </SelectControl>
              <TextFieldControl
                control={control}
                name="streetAndHouseNumber"
                label={t('tenantModule:paymentSettings.fields.tenantStreet')}
                rules={{ required: true }}
                data-test-id="bikeleasing-integration-streetAndHouseNumber-input"
              />
              <Stack direction="row" spacing={2} justifyContent="space-between">
                <TextFieldControl
                  control={control}
                  name="postalCode"
                  label={t('tenantModule:paymentSettings.fields.tenantPostalCode')}
                  rules={{ required: true }}
                  data-test-id="bikeleasing-integration-postalCode-input"
                  fullWidth
                />
                <TextFieldControl
                  control={control}
                  name="city"
                  label={t('tenantModule:paymentSettings.fields.tenantCity')}
                  rules={{ required: true }}
                  data-test-id="bikeleasing-integration-city-input"
                  fullWidth
                />
              </Stack>
              <SelectControl
                control={control}
                name="country"
                rules={{ required: true }}
                margin="dense"
                dataTestId="tenantAddressCountry"
                label={t('tenantModule:paymentSettings.fields.tenantAddressCountry')}
                defaultValue={tenant?.addressCountry ?? tenant?.countryCode}
              >
                {Object.values(TenantCountryCodeEnum).map(form => (
                  <MenuItem key={form} value={form}>
                    {t(`common:countryCode.${form}`)}
                  </MenuItem>
                ))}
              </SelectControl>
              <CheckboxControl
                control={control}
                name="olderThan1Year"
                rules={{ required: true }}
                label={<Typography variant="body2">{t('bikeleasing_v2.integration.form.olderThan1Year')}</Typography>}
              />
            </Stack>
          </form>
        </Stack>
      }
      actions={
        <Box>
          <Button
            type="button"
            onClick={dialogState.handleClose}
            sx={{ marginRight: 2 }}
            variant="text"
            data-test-id="modal-bikeleasing-customer-dialog-button-close"
          >
            {t('common:buttons.abort')}
          </Button>
          <DialogLoadingButton
            type="button"
            variant="contained"
            onClick={handleSubmit(handleClick)}
            loading={isPending}
            disabled={!formState.isValid}
            data-test-id="modal-bikeleasing-customer-dialog-button-confirm"
          >
            {t('common:buttons.requestNow')}
          </DialogLoadingButton>
        </Box>
      }
    />
  );
};
